import { useState, useCallback } from "react";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import { CollectionOrder } from "../../../api/collection";
import useCollectionOrdersQuery from "../hooks/useCollectionOrdersQuery";
import CollectionOrderModal from "./CollectionOrderModal";
import { debounce } from "lodash";
import DSBox from "../../system-design/DSBox";
import FilterBar from "components/FilterBar";
import DSChip from "../../system-design/DSChip";
import DSCircularProgress from "../../system-design/DSCircularProgress";

export type CollectionOrderRow = {
  orderName: string;
  status: string;
  orderDate: string;
  customerEmail: string;
  customerName?: string;
  collectionsCount: number;
};

function CollectionOrders() {
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<CollectionOrder | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [sortState, setSortState] = useState<Record<string, 1 | -1>>({});

  const handleFreeSearch = useCallback(
    debounce((s: string) => setTextSearch(s), 500),
    [],
  );

  const {
    data: orders,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCollectionOrdersQuery(textSearch, 20, sortState);

  const orderTableColumns: Column<CollectionOrder>[] = [
    { label: "Order Name", field: "orderName", sortable: true },
    { label: "Status", field: "status", sortable: true },
    { label: "Order Date", field: "orderCreationDate", sortable: true },
    { label: "Customer Email", field: "customerEmail", sortable: true },
    { label: "Customer Name", field: "customerName", sortable: true },
    { label: "Collections", field: "collections", sortable: true },
  ];

  const rowBuilder = (
    order: CollectionOrder,
    columns: Column<CollectionOrder>[],
  ) => {
    return (
      <>
        {columns.map((col) => {
          if (col.field === "orderName") {
            return (
              <DSTableCell key={col.field as string}>
                {order.orderName}
              </DSTableCell>
            );
          }
          if (col.field === "status") {
            return (
              <DSTableCell key={col.field as string}>
                <DSChip
                  size="small"
                  color={order.status === "completed" ? "success" : "warning"}
                  label={order.status}
                />
              </DSTableCell>
            );
          }
          if (col.field === "orderCreationDate") {
            return (
              <DSTableCell key={col.field as string}>
                {new Date(order.orderCreationDate).toLocaleDateString()}
              </DSTableCell>
            );
          }
          if (col.field === "collections") {
            return (
              <DSTableCell key={col.field as string}>
                {order.collections.length}
              </DSTableCell>
            );
          }
          if (col.field === "customerEmail") {
            return (
              <DSTableCell key={col.field as string}>
                {order.customerEmail}
              </DSTableCell>
            );
          }
          if (col.field === "customerName") {
            return (
              <DSTableCell key={col.field as string}>
                {order.customerName || "-"}
              </DSTableCell>
            );
          }
          return <DSTableCell key={col.field as string}>-</DSTableCell>;
        })}
      </>
    );
  };

  const handleRowClick = (order: CollectionOrder) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const handleSort = (field: keyof CollectionOrder) => {
    setSortState((prevState) => {
      const currentSort = prevState[field as string];
      if (!currentSort) {
        return { [field]: 1 };
      } else if (currentSort === 1) {
        return { [field]: -1 };
      } else {
        const newState = { ...prevState };
        delete newState[field as string];
        return newState;
      }
    });
  };

  if (isLoading && !orders?.length) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", p: "50px" }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  return (
    <>
      <FilterBar
        search={{
          value: searchInput,
          onChange: (v) => {
            setSearchInput(v);
            handleFreeSearch(v);
          },
          placeholder: "Search by order name or email...",
        }}
      />

      {orders && (
        <DSBaseTable
          data={orders}
          columns={orderTableColumns}
          rowBuilder={rowBuilder}
          onPressRow={handleRowClick}
          onSort={handleSort}
          sortState={sortState}
          fetchMore={
            hasNextPage && !isFetchingNextPage ? fetchNextPage : undefined
          }
        />
      )}

      <CollectionOrderModal
        show={showModal}
        order={selectedOrder}
        onClose={() => {
          setShowModal(false);
          setSelectedOrder(null);
        }}
      />
    </>
  );
}

export default CollectionOrders;
